import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Bookmark, Plus, Trash2, Search, RefreshCw, Filter } from 'lucide-react';

function EntityTypeBadge({ type }) {
  const colors = {
    PARTICIPANT: 'bg-blue-500/10 text-blue-600',
    STAFF: 'bg-purple-500/10 text-purple-600',
    HOUSE: 'bg-green-500/10 text-green-600',
    FORECAST: 'bg-amber-500/10 text-amber-600',
    RISK: 'bg-red-500/10 text-red-600',
  };
  return (
    <Badge variant="secondary" className={colors[type] || 'bg-muted'}>
      {type}
    </Badge>
  );
}

export default function SavedInsightsPage() {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    entityType: 'PARTICIPANT',
    branchId: '',
    dateRangeStart: '',
    dateRangeEnd: '',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (entityTypeFilter !== 'all') params.entityType = entityTypeFilter;
      const res = await api.get('/insights', { params });
      setInsights(res.data.items || []);
    } catch (err) {
      toast.error('Failed to load saved insights');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [entityTypeFilter]);

  const handleCreate = async () => {
    try {
      const payload = {
        name: form.name,
        entityType: form.entityType,
        branchId: form.branchId || null,
        filters: {},
        dateRange: form.dateRangeStart && form.dateRangeEnd ? {
          start: form.dateRangeStart,
          end: form.dateRangeEnd
        } : null
      };
      await api.post('/insights', payload);
      toast.success('Insight saved');
      setCreateOpen(false);
      setForm({ name: '', entityType: 'PARTICIPANT', branchId: '', dateRangeStart: '', dateRangeEnd: '' });
      fetchData();
    } catch (err) {
      toast.error('Failed to save insight');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this saved insight?')) return;
    try {
      await api.delete(`/insights/${id}`);
      toast.success('Insight deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete insight');
    }
  };

  const filtered = insights.filter(i => 
    !search || i.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="saved-insights-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bookmark className="h-6 w-6" />
            Saved Insights
          </h1>
          <p className="text-muted-foreground">Your saved insight views and filters</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-insight-btn">
          <Plus className="h-4 w-4 mr-2" />
          Save New Insight
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search insights..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Select value={entityTypeFilter} onValueChange={setEntityTypeFilter}>
              <SelectTrigger className="w-48" data-testid="entity-type-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="PARTICIPANT">Participant</SelectItem>
                <SelectItem value="STAFF">Staff</SelectItem>
                <SelectItem value="HOUSE">House</SelectItem>
                <SelectItem value="FORECAST">Forecast</SelectItem>
                <SelectItem value="RISK">Risk</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Your Saved Insights</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Entity Type</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead>Date Range</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-20">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No saved insights found
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((insight) => (
                    <TableRow key={insight.id} data-testid={`insight-row-${insight.id}`}>
                      <TableCell className="font-medium">{insight.name}</TableCell>
                      <TableCell>
                        <EntityTypeBadge type={insight.entityType} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {insight.branchId || '-'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {insight.dateRange ? 
                          `${insight.dateRange.start} - ${insight.dateRange.end}` : 
                          '-'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {insight.createdAt?.slice(0, 10)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(insight.id)}
                          data-testid={`delete-btn-${insight.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-insight-dialog">
          <DialogHeader>
            <DialogTitle>Save New Insight</DialogTitle>
            <DialogDescription>
              Save your current view configuration for quick access later
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="My Insight View"
                data-testid="insight-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Entity Type</Label>
              <Select value={form.entityType} onValueChange={(v) => setForm({ ...form, entityType: v })}>
                <SelectTrigger data-testid="insight-entity-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PARTICIPANT">Participant</SelectItem>
                  <SelectItem value="STAFF">Staff</SelectItem>
                  <SelectItem value="HOUSE">House</SelectItem>
                  <SelectItem value="FORECAST">Forecast</SelectItem>
                  <SelectItem value="RISK">Risk</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Branch ID (optional)</Label>
              <Input
                value={form.branchId}
                onChange={(e) => setForm({ ...form, branchId: e.target.value })}
                placeholder="MAIN"
                data-testid="insight-branch-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date From</Label>
                <Input
                  type="date"
                  value={form.dateRangeStart}
                  onChange={(e) => setForm({ ...form, dateRangeStart: e.target.value })}
                  data-testid="insight-date-start"
                />
              </div>
              <div className="space-y-2">
                <Label>Date To</Label>
                <Input
                  type="date"
                  value={form.dateRangeEnd}
                  onChange={(e) => setForm({ ...form, dateRangeEnd: e.target.value })}
                  data-testid="insight-date-end"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!form.name} data-testid="save-insight-btn">
              Save Insight
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
